/**
 * ChatTypingIndicator
 *
 * Renders a lightweight composer-adjacent typing indicator for remote chat
 * participants.
 *
 * Primary responsibilities:
 * - Format one or more remote typing names into a readable label
 * - Display a small animated typing affordance above the composer
 *
 * Dependencies:
 * - lib/hooks/useChatTypingPresence
 * - lib/utils
 */

'use client'

import type { ChatTypingParticipant } from '@/lib/hooks/useChatTypingPresence'
import { cn } from '@/lib/utils'

interface ChatTypingIndicatorProps {
  className?: string
  typers: ChatTypingParticipant[]
}

function formatTypingLabel(typers: ChatTypingParticipant[]) {
  if (typers.length === 0) {
    return ''
  }

  if (typers.length === 1) {
    return `${typers[0].displayName} is typing`
  }

  if (typers.length === 2) {
    return `${typers[0].displayName} and ${typers[1].displayName} are typing`
  }

  const additionalCount = typers.length - 2
  const additionalLabel = additionalCount === 1 ? 'other' : 'others'

  return `${typers[0].displayName}, ${typers[1].displayName}, and ${additionalCount} ${additionalLabel} are typing`
}

export function ChatTypingIndicator({
  className,
  typers
}: ChatTypingIndicatorProps) {
  if (typers.length === 0) {
    return null
  }

  return (
    <div
      aria-live="polite"
      className={cn(
        'text-muted-foreground flex items-center gap-2 px-3 pb-2 text-xs',
        className
      )}
      role="status"
    >
      <div
        aria-hidden="true"
        className="bg-muted/70 flex items-center gap-1 rounded-full px-2 py-1"
      >
        <span className="bg-primary/80 size-1.5 animate-bounce rounded-full [animation-delay:-0.3s]" />
        <span className="bg-primary/80 size-1.5 animate-bounce rounded-full [animation-delay:-0.15s]" />
        <span className="bg-primary/80 size-1.5 animate-bounce rounded-full" />
      </div>
      <span className="truncate">{formatTypingLabel(typers)}...</span>
    </div>
  )
}
